import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import { Box, BoxProps, Divider, Link, Paper, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Typography, useTheme } from '@mui/material';

import CopyToClipboardButton from '../../components/Clipboard/CopyToClipboardButton';
import { codeFontFamily, getCodeSyntaxTheme } from '../../themes/codeTokens';

interface StyledMarkdownProps extends Omit<BoxProps, 'children'> {
	children?: string;
	sx?: SxProps<Theme>;
}

interface MarkdownCodeElementProps {
	className?: string;
	children?: React.ReactNode;
}

const getCodeText = (children: React.ReactNode) => String(children).replace(/\n$/, '');

const renderCodeBlock = (children: React.ReactNode, syntaxTheme: ReturnType<typeof getCodeSyntaxTheme>) => {
	const codeElement = React.Children.toArray(children).find(React.isValidElement) as React.ReactElement<MarkdownCodeElementProps> | undefined;
	const codeProps = codeElement?.props ?? {};
	const className = typeof codeProps.className === 'string' ? codeProps.className : undefined;
	const language = /language-(\w+)/.exec(className ?? '')?.[1] ?? 'text';
	const code = getCodeText(codeProps.children ?? children);

	return (
		<Paper
			variant="outlined"
			sx={{
				my: 1,
				overflow: 'hidden',
				bgcolor: 'background.default',
				borderColor: 'divider',
				borderRadius: 1,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					px: 1.25,
					py: 0.5,
					bgcolor: 'action.hover',
					borderBottom: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', textTransform: 'uppercase' }}>
					{language}
				</Typography>
				<CopyToClipboardButton
					value={code}
					size="small"
					iconSize={14}
					edge={false}
					title="Copy code to clipboard"
					sx={{ color: 'text.secondary', p: 0.25, '&:hover': { color: 'text.primary', bgcolor: 'action.selected' } }}
				/>
			</Box>
			<Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
				<SyntaxHighlighter
					language={language}
					style={syntaxTheme}
					PreTag="div"
					customStyle={{
						margin: 0,
						padding: '12px 14px',
						background: 'transparent',
						fontFamily: codeFontFamily,
						fontSize: '0.78rem',
						lineHeight: 1.55,
						whiteSpace: 'pre',
					}}
					codeTagProps={{
						style: {
							fontFamily: 'inherit',
						},
					}}
				>
					{code}
				</SyntaxHighlighter>
			</Box>
		</Paper>
	);
};

const StyledMarkdown: React.FC<StyledMarkdownProps> = ({ children, sx, ...rest }) => {
	const syntaxTheme = getCodeSyntaxTheme(useTheme());

	return (
	<Box sx={sx} {...rest}>
		<Markdown
			remarkPlugins={[remarkGfm]}
			components={{
				p: ({ children }) => (
					<Typography variant="body2" sx={{ mt: 0.5, mb: 0.5 }}>
						{children}
					</Typography>
				),
				h1: ({ children }) => (
					<Typography variant="h5" gutterBottom>
						{children}
					</Typography>
				),
				h2: ({ children }) => (
					<Typography variant="h6" gutterBottom>
						{children}
					</Typography>
				),
				h3: ({ children }) => (
					<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
						{children}
					</Typography>
				),
				li: ({ children }) => (
					<Typography variant="body2" component="li">
						{children}
					</Typography>
				),
				hr: () => <Divider sx={{ my: 1 }} />,
				code: ({ children, className }) => (
					<Box
						component="code"
						className={className}
						sx={{
							fontFamily: codeFontFamily,
							bgcolor: 'action.hover',
							color: 'text.primary',
							border: '1px solid',
							borderColor: 'divider',
							px: 0.5,
							py: 0.1,
							borderRadius: 0.75,
							fontSize: '0.85em',
							fontWeight: 500,
							lineHeight: 1.5,
							whiteSpace: 'pre-wrap',
							overflowWrap: 'anywhere',
							boxDecorationBreak: 'clone',
							WebkitBoxDecorationBreak: 'clone',
						}}
					>
						{children}
					</Box>
				),
				a: ({ href, children }) => href ? (
					<Link href={href} target="_blank" rel="noopener noreferrer" underline="always" sx={{ overflowWrap: 'anywhere' }}>
						{children}
					</Link>
				) : <span>{children}</span>,
				strong: ({ children }) => (
					<Typography variant="body2" component="strong" sx={{ fontWeight: 'bold' }}>
						{children}
					</Typography>
				),
				em: ({ children }) => (
					<Typography variant="body2" component="em" sx={{ fontStyle: 'italic' }}>
						{children}
					</Typography>
				),
				blockquote: ({ children }) => (
					<Typography component="blockquote" sx={{ borderLeft: 4, borderColor: 'divider', pl: 2, ml: 0, my: 1, color: 'text.secondary' }}>
						{children}
					</Typography>
				),
				table: ({ children }) => (
					<TableContainer component={Paper} variant="outlined" sx={{ my: 1.25, maxWidth: '100%', overflowX: 'auto' }}>
						<Table size="small" sx={{ minWidth: 520 }}>
							{children}
						</Table>
					</TableContainer>
				),
				thead: ({ children }) => <TableHead>{children}</TableHead>,
				tbody: ({ children }) => <TableBody>{children}</TableBody>,
				tr: ({ children }) => <TableRow>{children}</TableRow>,
				th: ({ children }) => (
					<TableCell component="th" sx={{ fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' }}>
						{children}
					</TableCell>
				),
				td: ({ children }) => (
					<TableCell sx={{ verticalAlign: 'top' }}>
						{children}
					</TableCell>
				),
				pre: ({ children }) => renderCodeBlock(children, syntaxTheme),
			}}
		>
			{children}
		</Markdown>
	</Box>
	);
};

export default React.memo(StyledMarkdown);
